// src/components/Navbar.js
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, LayoutDashboard, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { normalizeRole, ROLE_VALUE } from "../constants/roleLabels";
import { BRAND } from "../config/brand";
import BrandMark from "./BrandMark";

/**
 * Public marketing navigation. Rendered only on the public-facing pages that
 * still live inside <Layout /> (home, submit-problem, profile, onboarding,
 * legal/blog pages, etc). Authenticated consoles (dashboards, workspace,
 * history) render their own OperationsShell chrome and never mount this.
 *
 * Still, a signed-in visitor can land on one of the Layout-wrapped pages
 * (e.g. /profile), so this bar is auth-aware: it swaps "Login / Signup" for
 * a direct link back to that person's console instead of ever offering to
 * log an already-authenticated person in again.
 */
function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const { currentUser, currentUserRole, logout } = useAuth();
  const navigate = useNavigate();

  const dashboardPath =
    normalizeRole(currentUserRole) === ROLE_VALUE.specialist
      ? "/dashboard-helper"
      : "/dashboard-client";

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    navigate("/");
  };

  const linkClass =
    "text-sm font-medium text-slate-600 hover:text-slate-900 transition";
  const mobileLinkClass =
    "py-2 px-2 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 transition";

  return (
    <nav className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto flex justify-between items-center px-4 py-3 md:px-6">
        <Link to="/" className="flex items-center gap-2">
          <BrandMark size={32} />
          <span className="text-base font-semibold text-slate-900">{BRAND.name}</span>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center gap-6">
          <Link to="/submit-problem" className={linkClass}>Submit a problem</Link>
          <Link to="/blog" className={linkClass}>Blog</Link>
          {!currentUser && (
            <Link to="/onboarding" className={linkClass}>Become an expert</Link>
          )}

          <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
            {currentUser ? (
              <>
                <Link
                  to={dashboardPath}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-slate-900 transition"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Console
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
                >
                  <LogOut className="w-4 h-4" />
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className={linkClass}>Log in</Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center rounded-md bg-blue-600 px-3.5 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 transition"
                >
                  Sign up
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Mobile Hamburger Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden text-slate-700 focus:outline-none"
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      {isOpen && (
        <div className="md:hidden border-t border-slate-200 px-4 py-3 flex flex-col gap-1">
          <Link to="/submit-problem" className={mobileLinkClass} onClick={() => setIsOpen(false)}>
            Submit a problem
          </Link>
          <Link to="/blog" className={mobileLinkClass} onClick={() => setIsOpen(false)}>
            Blog
          </Link>
          {!currentUser && (
            <Link to="/onboarding" className={mobileLinkClass} onClick={() => setIsOpen(false)}>
              Become an expert
            </Link>
          )}
          <div className="mt-2 pt-2 border-t border-slate-200 flex flex-col gap-1">
            {currentUser ? (
              <>
                <Link
                  to={dashboardPath}
                  className={mobileLinkClass}
                  onClick={() => setIsOpen(false)}
                >
                  Go to console
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className={`${mobileLinkClass} text-left`}
                >
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className={mobileLinkClass} onClick={() => setIsOpen(false)}>
                  Log in
                </Link>
                <Link to="/signup" className={mobileLinkClass} onClick={() => setIsOpen(false)}>
                  Sign up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
